/**
 * ONE BONE — one split-payment line, two providers' own modals.
 *
 * The product page carries two BNPL notices: Sezzle's, injected by its app
 * embed, and Shopify's `payment_terms`. Neither one's copy is ours to rewrite,
 * and each carries the disclosure a shopper needs before taking credit — so
 * this does not replace them. It hides them and hands their modals to the one
 * consolidated line in snippets/split-payment-terms.liquid.
 *
 * Nothing is consolidated until both providers' triggers are in hand. Until
 * then — and forever, if a provider's markup has moved — the two official
 * notices are what the shopper sees. A line reading "4 payments of $14.75"
 * with a logo that opens nothing would be worse than the duplication it fixes.
 *
 * The consolidation is a single class on <body>, so the CSS survives
 * variant-select.js replacing the price container's innerHTML, and clicks are
 * delegated from the document for the same reason.
 *
 * Both providers' notices stay in the accessibility tree while clipped. A
 * screen reader hears our line and then theirs, which is wordy, and the
 * alternative is hiding a credit disclosure from the people most reliant on it.
 */
(() => {
  'use strict';

  if (window.OneBoneSplitPayment) return;

  const BRIDGED_CLASS = 'split-payment-bridged';
  const HIDDEN_CLASS = 'split-payment-hidden';

  /**
   * Where each provider keeps the thing that opens its modal.
   *
   * `notice` is the block to clip; `triggers` are tried in order, and the
   * first match that exists wins. These are third-party DOM: they are the
   * reason this whole thing is written to fall back rather than assume.
   */
  const PROVIDERS = {
    sezzle: {
      notice: [
        '.sezzle-shopify-info-button',
        '.sezzle-widget',
        '[class*="sezzlewidget"]',
        '[class*="sezzle-shopify-info"]'
      ],
      triggers: [
        '.sezzle-info-icon',
        '.sezzle-question-mark-icon',
        '.sezzle-learn-more',
        '.sezzle-modal-link',
        '[class*="sezzle"] [class*="info-icon"]',
        '[class*="sezzle"] a'
      ]
    },
    shop: {
      notice: ['.shopify-payment-terms'],
      // The shadow root is on the custom element, not on the wrapper div that
      // `notice` clips -- looking for it on the wrapper is what stopped this
      // resolving the first time round.
      shadowHost: 'shopify-payment-terms',
      triggers: ['shopify-payment-terms button', 'shopify-payment-terms a'],
      shadowTriggers: [
        'button',
        '[role="button"]',
        'a',
        '[data-testid*="modal"]',
        '[aria-haspopup]'
      ]
    }
  };

  const firstMatch = (selectors, root = document) => {
    for (const selector of selectors) {
      const found = root.querySelector(selector);
      if (found) return found;
    }
    return null;
  };

  /**
   * Shopify keeps its "learn more" control inside the shadow root of
   * <shopify-payment-terms>, so a provider can name a `shadowHost` to be
   * searched when nothing in the light DOM matches. An open root is
   * reachable; a closed one is a clean miss rather than something to work
   * around.
   */
  const findTrigger = (provider) => {
    const direct = firstMatch(provider.triggers);
    if (direct) return direct;
    if (!provider.shadowHost) return null;

    const host = document.querySelector(provider.shadowHost);
    if (!host || !host.shadowRoot) return null;

    return firstMatch(provider.shadowTriggers || ['button', '[role="button"]', 'a'], host.shadowRoot);
  };

  const resolved = {};

  const resolveAll = () => {
    for (const [name, provider] of Object.entries(PROVIDERS)) {
      if (resolved[name]) continue;

      const notice = firstMatch(provider.notice);
      const trigger = findTrigger(provider);
      if (notice && trigger) resolved[name] = { notice, trigger };
    }
    return Object.keys(resolved).length === Object.keys(PROVIDERS).length;
  };

  const consolidate = () => {
    Object.values(resolved).forEach(({ notice }) => notice.classList.add(HIDDEN_CLASS));
    document.body.classList.add(BRIDGED_CLASS);
  };

  /**
   * Both notices arrive late and by different routes, so this polls rather
   * than watching for mutations.
   *
   * A MutationObserver was the obvious choice and it was the wrong one: it
   * cannot see inside a shadow root. Shopify populates the shadow root of
   * <shopify-payment-terms> after the component upgrades, and that produces no
   * mutation in the light DOM at all. So the observer would wake when Sezzle
   * injected its widget, find Shopify's root still empty, and then never be
   * woken again -- there was nothing left to mutate. Two notices, every time.
   *
   * Polling has no such blind spot. Fifty passes of a handful of
   * querySelectors is nothing next to being wrong.
   *
   * It gives up at DEADLINE_MS: a shopper who has been reading two notices for
   * ten seconds is not helped by them collapsing under the cursor.
   */
  const DEADLINE_MS = 10000;
  const INTERVAL_MS = 200;

  // A swap can start a second watch while the first is still ticking; only the
  // newest one is allowed to finish.
  let generation = 0;

  const watch = () => {
    const mine = ++generation;
    const started = Date.now();

    const tick = () => {
      if (mine !== generation) return;

      if (resolveAll()) {
        consolidate();
        return;
      }

      if (Date.now() - started >= DEADLINE_MS) {
        // Stand down rather than leave our line up with logos that open
        // nothing. On first load this is a no-op; after a swap it is the
        // difference between one notice pair and a broken line.
        document.body.classList.remove(BRIDGED_CLASS);

        const missing = Object.keys(PROVIDERS).filter((name) => !resolved[name]);
        console.warn(
          '[split-payment] Not consolidated: no modal trigger found for ' +
            missing.join(', ') +
            '. Showing each provider\'s own notice instead. ' +
            'window.OneBoneSplitPayment.debug() lists what it could see.'
        );
        return;
      }

      window.setTimeout(tick, INTERVAL_MS);
    };

    tick();
  };

  /**
   * assets/colourway-swap.js replaces <main> when a colourway is picked and
   * announces shopify:section:load. Our line comes back from the server -- but
   * so do both providers' notices, as new elements without the class the script
   * put on the old ones, and with the triggers we held now detached. That is
   * three banners: theirs, ours, theirs.
   *
   * So the references are dropped and the resolve runs again. The CSS keeps the
   * notices hidden in the meantime, keyed on the body class rather than on the
   * elements, so nothing flashes while the new widgets arrive.
   */
  const reset = () => {
    Object.keys(resolved).forEach((key) => delete resolved[key]);
    watch();
  };

  /**
   * What each provider looked like when asked. `candidates` enumerates
   * everything in the shadow root that could plausibly be the control, so a
   * missed selector is one console call away rather than a round trip.
   */
  const debug = () => {
    const describe = (el) =>
      el
        ? el.tagName.toLowerCase() +
          (el.dataset && el.dataset.testid ? '[data-testid=' + el.dataset.testid + ']' : '') +
          (el.getAttribute('class') ? '.' + el.getAttribute('class').split(/\s+/).join('.') : '')
        : null;

    const report = {};

    for (const [name, provider] of Object.entries(PROVIDERS)) {
      const host = provider.shadowHost ? document.querySelector(provider.shadowHost) : null;
      const root = host && host.shadowRoot;
      const trigger = findTrigger(provider);

      report[name] = {
        resolved: Boolean(resolved[name]),
        notice: describe(firstMatch(provider.notice)),
        shadowHost: provider.shadowHost ? (host ? 'present' : 'missing') : 'n/a',
        shadowRoot: provider.shadowHost ? (root ? 'open' : 'closed or not attached') : 'n/a',
        trigger: trigger ? describe(trigger) : null,
        triggerHTML: trigger ? trigger.outerHTML.slice(0, 200) : null,
        candidates: root
          ? Array.from(
              root.querySelectorAll('button, a, [role="button"], [aria-haspopup], [data-testid], svg')
            )
              .map(describe)
              .slice(0, 25)
          : null
      };
    }

    console.log(report);
    return report;
  };

  // Delegated, so a variant change re-rendering the price container cannot
  // leave a logo unwired.
  document.addEventListener('click', (event) => {
    const target = event.target.closest('[data-split-payment-provider]');
    if (!target) return;

    const name = target.dataset.splitPaymentProvider;
    let entry = resolved[name];

    // A swap may have replaced the trigger since it was resolved.
    if (entry && !entry.trigger.isConnected) {
      delete resolved[name];
      resolveAll();
      entry = resolved[name];
    }
    if (!entry) return;

    event.preventDefault();
    entry.trigger.click();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const target = event.target.closest?.('[data-split-payment-provider]');
    if (!target || !resolved[target.dataset.splitPaymentProvider]) return;

    event.preventDefault();
    target.click();
  });

  window.OneBoneSplitPayment = { watch, reset, debug, resolved };

  document.addEventListener('shopify:section:load', reset);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch);
  } else {
    watch();
  }
})();
